/**
 * Session state: tokens, the current principal, and the permission and feature maps
 * derived from them.
 *
 * Server *data* belongs to TanStack Query. This store holds only session and preference
 * state — the things that outlive a query cache and that the router needs synchronously
 * in order to decide what to render.
 */

import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

import { api, configureSession, onExpiry, onRefresh } from '@/lib/api-client';
import type { CurrentUser, FlagState, LoginResponse, TokenPair } from '@/types/api';

/*
 * ---- Where the session survives to -------------------------------------------
 *
 * "Remember me" on the sign-in form is the only thing that decides it. Checked, the
 * token pair goes to localStorage and outlives the browser; unchecked, it goes to
 * sessionStorage and dies with the tab — which is what the box has to mean on a
 * shared or public terminal, the case it exists for.
 *
 * The preference itself is kept in localStorage rather than in the persisted state:
 * it has to be readable *before* the store hydrates in order to choose which store
 * to hydrate from, and it is a UI preference, not a credential.
 *
 * Reads fall back from session to local so an existing remembered session is still
 * found. Writes go to exactly one of the two and delete the other copy, which is the
 * load-bearing half — without it, unchecking the box would leave the previous
 * remembered token sitting in localStorage where the reader asked for it not to be.
 */

const REMEMBER_KEY = 'lms.remember';

/** Default on: it is the behaviour the product had before the box existed. */
function isRemembered(): boolean {
  try {
    return window.localStorage.getItem(REMEMBER_KEY) !== 'false';
  } catch {
    // Storage blocked by policy or by a private-mode quota. The session then lives
    // in memory for this tab only, which is the safe direction to fail in.
    return true;
  }
}

/**
 * Record the reader's choice.
 *
 * Called before the credentials are posted, so that the `set()` the successful login
 * performs is already writing to the store the reader picked.
 */
export function setRememberSession(remember: boolean): void {
  try {
    window.localStorage.setItem(REMEMBER_KEY, String(remember));
  } catch {
    /* Nothing to record against; `isRemembered` will keep returning the default. */
  }
}

const sessionAwareStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.sessionStorage.getItem(name) ?? window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      const [keep, discard] = isRemembered()
        ? [window.localStorage, window.sessionStorage]
        : [window.sessionStorage, window.localStorage];

      keep.setItem(name, value);
      discard.removeItem(name);
    } catch {
      /* See `isRemembered` — an unwritable store degrades to a memory-only session. */
    }
  },
  removeItem: (name) => {
    try {
      window.sessionStorage.removeItem(name);
      window.localStorage.removeItem(name);
    } catch {
      /* Already unreachable; there is nothing left to clear. */
    }
  },
};

interface AuthState {
  access: string | null;
  refresh: string | null;
  tenantSlug: string | null;
  user: CurrentUser | null;
  /** True until the initial session restore has finished, so guards do not flash. */
  isRestoring: boolean;

  setTenantSlug: (slug: string) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  restore: () => Promise<void>;
  applyTokens: (tokens: TokenPair) => void;
  clear: () => void;

  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
  featureState: (flag: string) => FlagState;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      access: null,
      refresh: null,
      tenantSlug: null,
      user: null,
      isRestoring: true,

      setTenantSlug: (slug) => {
        set({ tenantSlug: slug });
        configureSession({ tenantSlug: slug });
      },

      login: async (email, password) => {
        const result = await api.post<LoginResponse>(
          '/auth/login/',
          { email, password },
          { anonymous: true },
        );

        configureSession({ access: result.access, refresh: result.refresh });
        set({ access: result.access, refresh: result.refresh, user: result.user });
      },

      logout: async () => {
        const { refresh } = get();
        try {
          if (refresh) await api.post('/auth/logout/', { refresh });
        } finally {
          // The local session is cleared whether or not the server acknowledged; a
          // user who pressed "sign out" must end up signed out.
          get().clear();
        }
      },

      restore: async () => {
        const { access, refresh, tenantSlug } = get();
        configureSession({ access, refresh, tenantSlug });

        if (!access && !refresh) {
          set({ isRestoring: false });
          return;
        }

        try {
          const user = await api.get<CurrentUser>('/auth/me/');
          set({ user, isRestoring: false });
        } catch {
          // The token was revoked, expired beyond refresh, or belongs to another
          // organisation. Either way there is no usable session to restore.
          get().clear();
          set({ isRestoring: false });
        }
      },

      applyTokens: (tokens) => {
        set({ access: tokens.access, refresh: tokens.refresh });
      },

      clear: () => {
        configureSession({ access: null, refresh: null });
        set({ access: null, refresh: null, user: null });
      },

      can: (permission) => Boolean(get().user?.permissions.includes(permission)),

      canAny: (permissions) => {
        const held = get().user?.permissions ?? [];
        return permissions.some((permission) => held.includes(permission));
      },

      featureState: (flag) => get().user?.features[flag] ?? 'disabled',
    }),
    {
      name: 'lms.session',
      storage: createJSONStorage(() => sessionAwareStorage),
      // The principal is deliberately not persisted: permissions and feature states can
      // change server-side at any moment, so they are re-fetched on every load rather
      // than restored from storage where they could be stale — or edited.
      partialize: (state) => ({
        access: state.access,
        refresh: state.refresh,
        tenantSlug: state.tenantSlug,
      }),
    },
  ),
);

/* The api client owns token rotation, so it reports the outcome back to the store. */
onRefresh((tokens) => useAuthStore.getState().applyTokens(tokens));
onExpiry(() => useAuthStore.getState().clear());
